import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { linkPaymentData } from '../../../data/linkPayment';
import useBeginPay from '../../../hooks/useBeginPay';
import usePaymentStepper from '../../../hooks/usePaymentStepper';
import useWindowSize from '../../../hooks/useWindowSize';
import { AppDispatch, RootState } from '../../../store/config';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setStep } from '../../../store/slices/step.slice';
import { BeginPay } from '../../../types/beginPay.types';
import { Commerce } from '../../../types/commerce.type';
import { TravelsDebt } from '../../../types/debt.types';
import { PaymentStep, VerticalType } from '../../../types/enums';
import { Issuer } from '../../../types/paymentMethod.types';
import { User } from '../../../types/user.types';
import Header from '../../Header';
import CustomSnackbar from '../../Snackbar/CustomSnackbar';
import ConfirmEmailDialog from '../../Stepper/ConfirmEmailDialog';

function TravelsHeader() {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const dispatch: AppDispatch = useAppDispatch();
  const {
    isSubmitDisabled,
    step,
    debtWithFeeFormattedCurrency,
    debtFormattedCurrency,
    handleGoBack,
    isPaymentLink,
  } = usePaymentStepper();
  const [isConfirmEmailDialogOpen, setIsConfirmEmailDialogOpen] = useState<boolean>(false);
  const commerce: Commerce | null = useAppSelector((state: RootState) => state.commerce.commerce);
  const selectedIssuer: Issuer | null = useAppSelector((state: RootState) => state.issuer.issuer);
  const debt: TravelsDebt = useAppSelector((state: RootState) => state.debt.travelsDebt);
  const user: User = useAppSelector((state: RootState) => state.user);

  const title =
    step !== 0
      ? `${t('verticals.travel_agency.payment-title')} / ${commerce?.name} / ${debt.booking}`
      : step === 0 && isPaymentLink
      ? `${t('verticals.travel_agency.payment-title')} / ${commerce?.name}`
      : `${t('verticals.travel_agency.payment-title')}`;

  const totalAmount =
    step !== 0 ? (selectedIssuer ? debtWithFeeFormattedCurrency : debtFormattedCurrency) : undefined;

  const { submitBeginPay, snackbarOpen, errorMessage, closeSnackbar } = useBeginPay();

  const submitPayment = () => {
    if (selectedIssuer?.issuerConfigurationId && commerce?.commerceId) {
      const beginPayValues: BeginPay = {
        verticalType: VerticalType.TRAVEL_AGENCY,
        commerceId: commerce?.commerceId,
        issuerConfigurationId: selectedIssuer.issuerConfigurationId,
        amount: +(debt.amount ?? 0),
        currency: debt.currency,
        data: {
          userName: user?.userName || linkPaymentData.userName,
          email: user.email || linkPaymentData.email,
          phone: user.phone || linkPaymentData.phone,
          documentType: user.documentType || linkPaymentData.documentType,
          document: user?.document || linkPaymentData.document,
          agency: debt.agency,
          booking: debt.booking,
        },
      };

      submitBeginPay(beginPayValues);
    }
  };

  const handleConfirmEmailDialogToggle = () => {
    setIsConfirmEmailDialogOpen(!isConfirmEmailDialogOpen);
  };

  const handleSubmit =
    !isPaymentLink && step === PaymentStep.PAYMENT_METHODS
      ? submitPayment
      : isPaymentLink && step === PaymentStep.EMAIL
      ? user.email !== null && user.email !== linkPaymentData.email
        ? submitPayment
        : handleConfirmEmailDialogToggle
      : isPaymentLink && step === PaymentStep.PAYMENT_METHODS
      ? () => dispatch(setStep(PaymentStep.EMAIL))
      : undefined;

  return (
    <>
      <Header
        title={title}
        form={'travels-debt-form'}
        hasBack={!!(user.userName && !isPaymentLink) || step !== 0}
        buttonText={
          step === PaymentStep.INFORMATION
            ? t('verticals.step-labels.edit-button')
            : t('verticals.step-labels.back-button')
        }
        goBack={handleGoBack}
        submit={handleSubmit}
        totalAmount={totalAmount}
        commerceId={commerce?.commerceId}
        verticalType={VerticalType.TRAVEL_AGENCY}
      />
      <CustomSnackbar
        message={errorMessage}
        severity="error"
        isOpen={snackbarOpen}
        handleClose={closeSnackbar}
        isMobile={isMobile}
      />
      <ConfirmEmailDialog
        isDialogOpen={isConfirmEmailDialogOpen}
        handleCloseDialog={handleConfirmEmailDialogToggle}
        title={t('verticals.step-labels.email.dialog.title')}
        contentText={t('verticals.step-labels.email.dialog.content')}
        onContinue={submitPayment}
        submitButtonDisabled={isSubmitDisabled}
      />
    </>
  );
}

export default TravelsHeader;
